import uuid from 'uuid/v4';
import UBT from '@/utils/ubt';
import Toast from '@/components/toast';
import tianqiUBT from '@/utils/tianqiUBT';
import { scriptJs, wrapScriptMethodsWithGuard } from '../crossOriginInstruments';
import env from '../env.js';
import params from '../param.js';
import appConst from '../../const/app.const';
import Storage from '../storage';

let loadPromise;
let mpLoadPromise;
let currentCode = null;
const APP_ID = appConst.alipayAppId;
function loadJSSDKScript() {
  if (!env.isAlipayClient) {
    return Promise.reject(new Error('not alipay'));
  }
  if (loadPromise) {
    return loadPromise;
  }
  loadPromise = new Promise((resolve, reject) => {
    scriptJs('//as.alipayobjects.com/g/component/antbridge/1.1.1/antbridge.min.js', () => {
      if (window.Ali) {
        resolve(window.Ali);
      } else {
        reject(new Error('load alipay jssdk failed'));
        UBT.sendError({ keyword: 'load_alipay_jssdk_failed' });
      }
    });
  });
  return loadPromise;
}

function loadMPJSSDKScript() {
  if (!env.isAlipayClient) {
    return Promise.reject(new Error('请在支付宝中打开此链接'));
  }
  if (mpLoadPromise) {
    return mpLoadPromise;
  }
  mpLoadPromise = new Promise((resolve, reject) => {
    scriptJs('https://appx/web-view.min.js', () => {
      if (window.my) {
        resolve(window.my);
        wrapScriptMethodsWithGuard(window.my, [
          'postMessage',
          'navigateBack',
          'tradePay',
          'getEnv',
        ]);
      } else {
        reject(new Error('load alipay mpjssdk failed'));
        UBT.sendError({ keyword: 'load_alipay_mpjssdk_failed' });
      }
    });
  });
  return mpLoadPromise;
}

function getAlipayUrl(info) {
  const orderInfo = info.split('&').map((paramStr) => {
    const [key, val] = paramStr.split('=');
    if (key === 'sign' || key === 'return_url') {
      return `${key}=${val}`;
    }
    return `${key}=${encodeURIComponent(val)}`;
  }).join('&');

  return `https://mapi.alipay.com/gateway.do?${orderInfo}`;
}

function bindAlipayMethod(methodName) {
  return (...args) => loadJSSDKScript()
    .then((Ali) => {
      Ali[methodName](...args);
    });
}

const _aliCallBacks = {};
if (env.isAlipayClient) {
  loadMPJSSDKScript().then(() => {
    window.my.onMessage = (data) => { // 此处data: {callbackid success}
      try {
        const _aliCallBack = _aliCallBacks[data.callbackId];
        if (_aliCallBack) {
          if (data.data) {
            if (data.callbackId) {
              // 此处那个callbackid的promise终于resolve并且将数据带给h5
              _aliCallBack.resolve(data.data);
            } else {
              _aliCallBack.reject(new Error());
            }
          } else if (!data.success) {
            _aliCallBack.reject(new Error((data.error && data.error.message)
              || (typeof data.error === 'string' ? data.error : JSON.stringify(data.error)) || 'unknown error'));
          } else {
            _aliCallBack.resolve();
          }
        } else {
          tianqiUBT.customEvent({
            eventId: 'hitch-main-script-error-alipay-callback-not-exit',
            callBackKeys: Object.keys(_aliCallBacks),
            data,
          });
        }
        delete _aliCallBacks[data.callbackId];
      } catch (error) {
        tianqiUBT.customEvent({
          eventId: 'hitch-main-script-error-alipay',
          callBackKeys: Object.keys(_aliCallBacks),
          data,
          error,
        });
      }
    };
  });
}

function callAliMessageBack(callBackFun) { // 此处入参是id
  return new Promise((resolve, reject) => { // 此处给一个pending的promise
    _aliCallBacks[callBackFun] = { resolve, reject };
  });
}

export default {
  loadMPJSSDKScript,
  async mpPostMessage(data) { // 此处是对postmessage的封装
    await loadMPJSSDKScript(); // 此处加载sdk
    if (data) {
      // eslint-disable-next-line no-param-reassign
      data.callbackId = `${data.callbackId}${uuid()}`;
    }
    window.my.postMessage(data); // postmessage data: { command callbackid data }
    return callAliMessageBack(data.callbackId);
  },
  async mpRelauch(url) {
    await loadMPJSSDKScript();
    window.my.postMessage({ command: 'reLaunch', data: url });
  },
  async mpRedirectTo(url) {
    await loadMPJSSDKScript();
    window.my.postMessage({ command: 'redirectTo', data: url });
  },

  /**
   * * 页面跳转
   * * 跳转原生页面|webview内的跳转
   * @param {string} url跳转链接
   */
  async mpNavigateTo(url) {
    if (!url) return;
    if (url.startsWith('http')) {
      window.location.href = url;
    } else if (url.startsWith('/')) {
      await loadMPJSSDKScript();
      window.my.postMessage({ command: 'navigateTo', data: url });
    } else if (process.env.VUE_APP_ENV !== 'pro') {
      Toast.show('非有效链接，请检查');
    }
  },
  /**
   * 跳转其他支付宝小程序
   * @param appId
   * @param path
   * @param extraData
   */
  async navigateToMiniProgram({ appId, path, extraData = {} }) {
    await loadMPJSSDKScript();
    window.my.postMessage({ command: 'navigateToMiniProgram', data: { appId, path, extraData } });
  },
  async mpNavigateBack(url) {
    await loadMPJSSDKScript();
    return new Promise((resolve, reject) => {
      if (!url) {
        window.my.navigateBack();
        resolve();
        return;
      }

      window.my.navigateBack({
        url: url.indexOf('/') === 0 ? url : `/${url}`,
        success: resolve,
        fail: reject,
      });
    });
  },
  async mpHideShareMenu() {
    await loadMPJSSDKScript();
    window.my.postMessage({
      command: 'hideShareMenu',
      callbackId: `hideShareMenu${(new Date()).getTime()}`,
      dataArray: [{}],
    });
  },
  async mpNavigateBackByDelta(delta = 1) {
    await loadMPJSSDKScript();
    window.my.navigateBack({
      delta,
    });
  },
  // 支付方法
  async mpPay(orderStr) {
    await loadMPJSSDKScript();
    return new Promise((resolve, reject) => {
      window.my.tradePay({
        orderStr,
        success(res) {
          if (res.resultCode === '9000') {
            resolve(res);
          } else {
            reject();
          }
        },
        fail(res) {
          reject(res);
        },
      });
    });
  },
  /**
  * 先需要判断env.isAlipayClient, 然后通过此方法异步获取是否在小程序中
  *
  * @returns promise
  */
  async isInMp() {
    await loadMPJSSDKScript();
    return new Promise((resolve) => {
      window.my.getEnv((res) => {
        resolve(res && res.miniprogram);
      });
    });
  },
  scanQRCode() {
    return loadJSSDKScript().then((Ali) => {
      if ((Ali.alipayVersion).slice(0, 3) < 8.1) {
        return Promise.reject(new Error('请升级支付宝到最新版'));
      }
      return new Promise((resolve, reject) => {
        Ali.scan({
          type: 'qr', // qr(二维码) / bar(条形码) / card(银行卡号)
        }, (result) => {
          if (result.errorCode) {
            if (result.errorCode === 11) {
              reject(new Error('操作失败'));
            }
            if (result.errorCode === 10) {
              reject(new Error('已取消扫码'));
            }
          } else {
            resolve(result.qrCode || result.barCode);
          }
        });
      });
    });
  },

  vibrate(opt) {
    return loadJSSDKScript().then((Ali) => {
      if ((Ali.alipayVersion).slice(0, 3) < 8.2) {
        return Promise.reject(new Error('请升级支付宝到最新版'));
      }
      Ali.vibration.vibrate(opt || { duration: 500 });
      return true;
    });
  },

  showOptionMenu: bindAlipayMethod('showOptionMenu'),

  hideOptionMenu: bindAlipayMethod('hideOptionMenu'),

  closeWebView: bindAlipayMethod('closeWebview'),

  setTitle: bindAlipayMethod('setTitle'),

  remoteLog: bindAlipayMethod('remoteLog'),

  startShare(opt, callback) {
    window.AlipayJSBridge.call('startShare', opt, callback);
  },

  shareToChannel(opt, callback) {
    window.AlipayJSBridge.call('shareToChannel', opt, callback);
  },

  pay(info) {
    window.location.href = getAlipayUrl(info);
  },

  startApp(options) {
    window.AlipayJSBridge.call('startApp', options);
  },

  redirectToGetCode(backUrl, scope) {
    const url = 'https://openauth.alipay.com/oauth2/publicAppAuthorize.htm';
    const encodedBackUrl = encodeURIComponent(backUrl);
    const redirectUri = `${url}?app_id=${APP_ID}&scope=${scope}&redirect_uri=${encodedBackUrl}`;
    window.location.assign(redirectUri);
  },

  alipayAuth({ bacUrl, scope, redirectIfNoCode = true }) {
    const promise = new Promise((resolve, reject) => {
      let chances = Number(Storage.getItem('chances') || 1);
      chances += 1;
      if (chances >= 4) {
        UBT.sendError({ keyword: 'get_alipay_auth_failed' });
        Storage.removeItem('chances');
        reject(new Error(`连续授权登录${chances - 1}次失败, 请关闭页面重试`));
        return;
      }
      const code = this.getCode(window.location.href);
      if (!code || currentCode === code) {
        if (redirectIfNoCode) {
          this.redirectToGetCode(bacUrl || window.location.href, scope || 'auth_user,bike_agrt');
          const error = new Error('未获取到code, 系统引导去获取code');
          error.cancelPromiseChain = true;
          reject(error);
        } else {
          const error = new Error('url中无code');
          error.cancelPromiseChain = true;
          reject(error);
        }
      } else {
        Storage.removeItem('chances');
        currentCode = code;
        resolve({ code });
      }
    });

    return new Promise((resolve, reject) => {
      promise.then(
        resolve,
        (error) => {
          // 吃掉cancelPromiseChian的error
          if (!error.cancelPromiseChain) {
            reject(error);
          }
        },
      );
    });
  },

  // 获取用户微信code
  getCode() {
    if (!this.auth_code) {
      const code = params.get('auth_code');
      this.auth_code = code;
      params.remove('auth_code');
    }
    return this.auth_code;
  },

  /**
   * * 保存图片
   * @param {string|array} images 单张或多项图片
   */
  async mpSaveImage(image) {
    await loadMPJSSDKScript();
    await window.my.postMessage({
      command: 'saveImage',
      callbackId: `saveImage${(new Date()).getTime()}`,
      dataArray: Array.isArray(image) ? image : [image],
    });
  },

  async makePhoneCall(tel) {
    await loadMPJSSDKScript();
    await window.my.postMessage({
      command: 'makePhoneCall',
      callbackId: `makePhoneCall${(new Date()).getTime()}`,
      data: tel,
    });
  },

  async bondMpassPay(orderStr) {
    await loadMPJSSDKScript();
    const callbackId = `bondMpassPay${(new Date()).getTime()}`;
    await window.my.postMessage({
      command: 'bondMpassPay',
      callbackId,
      data: orderStr,
    });
    return callAliMessageBack(callbackId);
  },
};
